/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================
*/

import { useEffect } from "react";
import { useLocation, NavLink } from "react-router-dom";
import PropTypes from "prop-types";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";

// Custom styles for the Sidenav
import SidenavRoot from "examples/Sidenav/SidenavRoot";

import estiaLogo from "assets/images/logos/estia.jpg";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context";

function Sidenav({ color, brand, username, subtitle, routes, ...rest }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } = controller;
  const location = useLocation();

  // Hide Sidenav on onboarding page
  if (location.pathname === "/onboarding" || location.pathname === "/Onboarding") {
    return null;
  }

  const collapseName = location.pathname.replace(/^\//, "");

  let textColor = "white";
  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
      setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
    }

    window.addEventListener("resize", handleMiniSidenav);
    handleMiniSidenav();

    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  // ROLE-BASED ROUTE FILTERING
  const userRole = sessionStorage.getItem("userRole") || localStorage.getItem("userRole");
  const filteredRoutes = routes.filter((route) => {
    if (!route.roles) return true;
    return route.roles.includes(userRole);
  });

  const renderRoutes = filteredRoutes.map(
    ({ type, name, icon, title, noCollapse, key, href, route, badge }) => {
      if (type === "collapse") {
        return href ? (
          <Link
            href={href}
            key={key}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}
          >
            <SidenavCollapse
              name={name}
              icon={icon}
              active={key === collapseName}
              noCollapse={noCollapse}
              badge={badge}
            />
          </Link>
        ) : (
          <NavLink key={key} to={route}>
            <SidenavCollapse name={name} icon={icon} active={key === collapseName} badge={badge} />
          </NavLink>
        );
      } else if (type === "title") {
        return (
          <MDTypography
            key={key}
            color={textColor}
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            pl={3}
            mt={2}
            mb={1}
            ml={1}
          >
            {title}
          </MDTypography>
        );
      } else if (type === "divider") {
        return (
          <Divider
            key={key}
            light={
              (!darkMode && !whiteSidenav && !transparentSidenav) ||
              (darkMode && !transparentSidenav && whiteSidenav)
            }
          />
        );
      }
      return null;
    }
  );

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
    >
      <MDBox pt={3} pb={1} px={4} textAlign="center">
        <MDBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <MDTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </MDTypography>
        </MDBox>
        <MDBox
          component={NavLink}
          to="/"
          display="flex"
          flexDirection="column"
          alignItems="center"
          textAlign="center"
        >
          {brand && <MDBox component="img" src={brand} alt="Brand" width="2rem" mb={0.5} />}
          <MDTypography component="h6" variant="h6" fontWeight="medium" color={textColor} mb={0}>
            {username}
          </MDTypography>
          {subtitle && (
            <MDTypography
              variant="caption"
              fontStyle="italic"
              mt={0}
              textAlign="center"
              sx={{
                textTransform: "uppercase",
                fontWeight: 1100,
                letterSpacing: "0.05em",
                color: "#ffffffff",
              }}
            >
              {subtitle}
            </MDTypography>
          )}
        </MDBox>
      </MDBox>
      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />
      <List>{renderRoutes}</List>
      <MDBox mt="auto">
        {!miniSidenav && (
          <MDBox
            sx={{
              px: 3,
              pb: 2,
              display: "flex",
              justifyContent: "center",
              gap: 3,
            }}
          >
            {[
              { label: "Profile", to: "/profile" },
              { label: "Support", to: "/support" },
            ].map(({ label, to }) => (
              <NavLink key={to} to={to} style={{ textDecoration: "none" }}>
                <MDBox
                  sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "rgba(255,255,255,0.6)",
                    letterSpacing: "0.3px",
                    transition: "color 0.15s",
                    "&:hover": { color: "#fff" },
                  }}
                >
                  {label}
                </MDBox>
              </NavLink>
            ))}
          </MDBox>
        )}
        <MDBox p={2} textAlign="center" sx={{ backgroundColor: "#000000" }}>
          <MDBox
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gap={0.5}
          >
            <MDBox component="img" src={estiaLogo} alt="Estia Logo" width="7rem" height="7rem" />
          </MDBox>
        </MDBox>
      </MDBox>
    </SidenavRoot>
  );
}

Sidenav.defaultProps = {
  color: "info",
  brand: "",
  subtitle: "",
};

Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  username: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
